// src/pages/WatchChannel.js
import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import Player from '../components/player';
import apiService from '../services/api';
import StorageService from '../utils/storage';
import LazyImage from '../components/LazyImage';
import tmdbService from '../services/tmdb';

const Container = styled.div`
  min-height: 100vh;
  background-color: #141414;
  color: #fff;
  display: flex;
  flex-direction: column;
`;

const PlayerWrapper = styled.div`
  position: relative;
  width: 100%;
  height: 100vh;
  background: #000;
  display: flex;
  flex-direction: column;
  cursor: none;

  &:hover {
    cursor: default;
  }
`;

const Header = styled.header`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  z-index: 100;
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.8) 0%, rgba(0, 0, 0, 0) 100%);
  backdrop-filter: blur(5px);
  padding: 10px 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 50px;
  opacity: 0;
  transition: opacity 0.3s ease;
  pointer-events: none;

  ${PlayerWrapper}:hover & {
    opacity: 1;
    pointer-events: auto;
  }
`;

const BackButton = styled(Link)`
  position: fixed;
  top: 20px;
  left: 20px;
  display: flex;
  align-items: center;
  color: white;
  text-decoration: none;
  font-weight: 500;
  font-size: 14px;
  opacity: 0.8;
  transition: all 0.2s ease;
  z-index: 101;
  pointer-events: auto;
  
  &:hover {
    opacity: 1;
    transform: translateX(-4px);
  }
  
  svg {
    margin-right: 8px;
    width: 20px;
    height: 20px;
  }
`;

const PlayerSection = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const PlayerControls = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 30px;
  background: linear-gradient(to top, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0) 100%);
  z-index: 10;
  opacity: 0;
  transition: opacity 0.3s ease;

  ${PlayerWrapper}:hover & {
    opacity: 1;
  }
`;

const ControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  margin-top: 15px;
`;

const ControlButton = styled.button`
  background: transparent;
  border: none;
  color: white;
  width: 48px;
  height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.2s ease;
  border-radius: 50%;

  &:hover {
    background: rgba(255, 255, 255, 0.2);
    transform: scale(1.1);
  }

  svg {
    width: 32px;
    height: 32px;
  }

  &.large {
    width: 56px;
    height: 56px;
    
    svg {
      width: 40px;
      height: 40px;
    }
  }
`;

const ProgressBar = styled.div`
  width: 100%;
  height: 6px;
  background: rgba(255, 255, 255, 0.2);
  border-radius: 3px;
  cursor: pointer;
  margin-bottom: 15px;
  position: relative;

  &:hover {
    height: 8px;
  }

  &::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: ${props => props.$progress || 0}%;
    background: #E50914;
    border-radius: 3px;
  }
`;

const TimeDisplay = styled.div`
  color: white;
  font-size: 14px;
  margin-left: 10px;
  min-width: 100px;
  display: ${props => props.$isChannel ? 'none' : 'block'};
`;

const VolumeControl = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  min-width: 120px;

  input[type="range"] {
    width: 100%;
    height: 4px;
    -webkit-appearance: none;
    background: rgba(255, 255, 255, 0.2);
    border-radius: 2px;
    outline: none;

    &::-webkit-slider-thumb {
      -webkit-appearance: none;
      width: 12px;
      height: 12px;
      border-radius: 50%;
      background: white;
      cursor: pointer;
    }
  }
`;

const InfoSection = styled.div`
  padding: 40px;
  max-width: 1200px;
  margin: 0 auto;
  width: 100%;
`;

const ContentHeader = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 30px;
  margin-bottom: 30px;
`;

const ContentPoster = styled.div`
  width: ${props => props.$isChannel ? '100px' : '180px'};
  height: ${props => props.$isChannel ? '100px' : '270px'};
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
  box-shadow: 0 5px 15px rgba(0, 0, 0, 0.3);
  display: ${props => props.$isChannel ? 'flex' : 'block'};
  align-items: ${props => props.$isChannel ? 'center' : 'initial'};
  justify-content: ${props => props.$isChannel ? 'center' : 'initial'};
  padding: ${props => props.$isChannel ? '10px' : '0'};
  margin-top: ${props => props.$isChannel ? '0' : '30px'};
  
  img {
    width: 100%;
    height: ${props => props.$isChannel ? 'auto' : '100%'};
    object-fit: ${props => props.$isChannel ? 'contain' : 'cover'};
    max-height: ${props => props.$isChannel ? '100%' : 'none'};
  }
`;

const ContentInfo = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  padding: ${props => props.$isChannel ? '20px 30px' : '30px'};
  background: linear-gradient(to bottom, rgba(0, 0, 0, 0.9) 0%, rgba(0, 0, 0, 0.5) 50%, rgba(0, 0, 0, 0) 100%);
  display: flex;
  align-items: center;
  gap: 20px;
  opacity: 0;
  transition: opacity 0.3s ease;
  height: ${props => props.$isChannel ? '160px' : 'auto'};
  margin-top: ${props => props.$isChannel ? '30px' : '0'};
  z-index: 10;

  ${PlayerWrapper}:hover & {
    opacity: 1;
  }
`;

const ContentDetails = styled.div`
  flex: 1;
  margin-top: ${props => props.$isChannel ? '0' : '30px'};
  
  h2 {
    margin: 0;
    font-size: ${props => props.$isChannel ? '20px' : '24px'};
    font-weight: 600;
    color: white;
  }
  
  p {
    margin: 5px 0 0;
    font-size: ${props => props.$isChannel ? '14px' : '16px'};
    color: rgba(255, 255, 255, 0.7);
  }
`;

const ChannelLogo = styled.div`
  width: 180px;
  height: 180px;
  border-radius: 8px;
  overflow: hidden;
  background: linear-gradient(135deg, #2a2a2a 0%, #1a1a1a 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 15px;
  
  img {
    max-width: 100%;
    max-height: 100%;
    object-fit: contain;
  }
  
  .placeholder {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background-color: #E50914;
    display: flex;
    align-items: center;
    justify-content: center;
    color: white;
    font-size: 32px;
    font-weight: bold;
  }
`;

const ContentTitle = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin: 0 0 10px;
`;

const ContentMeta = styled.div`
  display: flex;
  align-items: center;
  gap: 15px;
  margin-bottom: 20px;
`;

const QualityBadge = styled.span`
  background-color: ${props => {
    switch(props.$quality) {
      case 'SD': return '#666';
      case 'HD': return '#4caf50';
      case 'FHD': return '#2196f3';
      case 'FHD H265': return '#1976d2';
      case 'FHD HDR': return '#0d47a1';
      case '4K': return '#E50914';
      case '4K H265': return '#b71c1c';
      case '4K HDR': return '#7f0000';
      default: return '#666';
    }
  }};
  color: white;
  font-size: 14px;
  font-weight: bold;
  padding: 4px 8px;
  border-radius: 4px;
`;

const CategoryBadge = styled.span`
  background-color: rgba(255, 255, 255, 0.1);
  color: #ccc;
  font-size: 14px;
  padding: 4px 8px;
  border-radius: 4px;
`;

const ContentDescription = styled.div`
  color: #ccc;
  font-size: 16px;
  line-height: 1.6;
  margin: 30px 0;
  max-width: 800px;
`;

const MetadataSection = styled.div`
  margin-top: 40px;
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 30px;
`;

const MetadataCard = styled.div`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  padding: 20px;
`;

const MetadataTitle = styled.h3`
  font-size: 16px;
  font-weight: 500;
  color: #999;
  margin: 0 0 10px;
`;

const MetadataContent = styled.div`
  color: white;
  font-size: 14px;
  
  ul {
    list-style: none;
    padding: 0;
    margin: 0;
    
    li {
      margin-bottom: 5px;
      
      &:last-child {
        margin-bottom: 0;
      }
    }
  }
`;

const CastList = styled.div`
  display: flex;
  gap: 20px;
  overflow-x: auto;
  padding: 20px 0;
  margin: 30px 0;
  
  &::-webkit-scrollbar {
    height: 5px;
  }
  
  &::-webkit-scrollbar-track {
    background: rgba(255, 255, 255, 0.1);
    border-radius: 3px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: rgba(255, 255, 255, 0.3);
    border-radius: 3px;
  }
`;

const CastCard = styled.div`
  flex: 0 0 120px;
  text-align: center;
`;

const CastImage = styled.div`
  width: 120px;
  height: 120px;
  border-radius: 60px;
  overflow: hidden;
  margin-bottom: 10px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const CastName = styled.div`
  font-size: 14px;
  font-weight: 500;
  margin-bottom: 5px;
`;

const CastRole = styled.div`
  font-size: 12px;
  color: #999;
`;

const Rating = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin-top: 10px;
  
  span {
    color: #ffd700;
    font-size: 24px;
  }
  
  div {
    font-size: 14px;
    color: #999;
  }
`;

const EpisodeList = styled.div`
  margin-top: 40px;
`;

const SeasonHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 20px;
  margin-bottom: 20px;
`;

const SeasonTitle = styled.h2`
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const SeasonSelect = styled.select`
  background-color: rgba(255, 255, 255, 0.1);
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 4px;
  font-size: 14px;
  cursor: pointer;
  
  &:focus {
    outline: none;
    background-color: rgba(255, 255, 255, 0.15);
  }
  
  option {
    background-color: #1a1a1a;
  }
`;

const EpisodeGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  gap: 20px;
`;

const EpisodeCard = styled(Link)`
  background-color: rgba(255, 255, 255, 0.1);
  border-radius: 4px;
  overflow: hidden;
  text-decoration: none;
  color: white;
  transition: all 0.2s ease;
  
  &:hover {
    transform: translateY(-4px);
    background-color: rgba(255, 255, 255, 0.15);
  }
`;

const EpisodeThumbnail = styled.div`
  width: 100%;
  aspect-ratio: 16/9;
  background-color: #1a1a1a;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const EpisodeInfo = styled.div`
  padding: 15px;
`;

const EpisodeTitle = styled.div`
  font-size: 16px;
  font-weight: 500;
  margin-bottom: 5px;
`;

const EpisodeNumber = styled.div`
  font-size: 14px;
  color: #999;
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 100vh;
  background: #141414;
  color: white;
  gap: 20px;

  svg {
    width: 120px;
    height: 120px;
  }

  p {
    font-size: 16px;
    font-weight: 500;
    text-shadow: 0 2px 4px rgba(0, 0, 0, 0.5);
  }
`;

const ErrorMessage = styled.div`
  color: #E50914;
  text-align: center;
  padding: 40px;
  
  h2 {
    font-size: 24px;
    margin-bottom: 10px;
  }
  
  p {
    font-size: 16px;
    color: #999;
  }
`;

const RetryButton = styled.button`
  background-color: #E50914;
  color: white;
  border: none;
  padding: 10px 20px;
  border-radius: 4px;
  font-size: 16px;
  cursor: pointer;
  transition: all 0.2s ease;
  
  &:hover {
    background-color: #c40813;
  }
`;

const GroupInfo = styled.div`
  font-size: 14px;
  color: #999;
`;

const Poster = styled.div`
  width: 100px;
  height: 150px;
  border-radius: 8px;
  overflow: hidden;
  background: rgba(0, 0, 0, 0.2);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 10px;
  margin-top: 30px;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

const WatchChannel = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isPlaying, setIsPlaying] = useState(true);
  const [volume, setVolume] = useState(0.5);
  const [progress, setProgress] = useState(0);
  const [isFavorite, setIsFavorite] = useState(false);
  const [duration, setDuration] = useState(0);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const playerRef = useRef(null);
  const playerWrapperRef = useRef(null);
  const [isControlsVisible, setIsControlsVisible] = useState(true);
  const hideControlsTimeout = useRef(null);

  // Função para detectar a qualidade
  const detectQuality = (name) => {
    const nameLower = name.toLowerCase();
    
    // Detecta qualidades compostas primeiro
    if (nameLower.includes('fhd') && nameLower.includes('hdr')) return 'FHD HDR';
    if (nameLower.includes('fhd') && nameLower.includes('h265')) return 'FHD H265';
    if (nameLower.includes('4k') && nameLower.includes('hdr')) return '4K HDR';
    if (nameLower.includes('4k') && nameLower.includes('h265')) return '4K H265';
    
    // Depois verifica qualidades simples
    if (nameLower.includes('4k')) return '4K';
    if (nameLower.includes('fhd') || nameLower.includes('fullhd') || nameLower.includes('full hd') || nameLower.includes('1080')) return 'FHD';
    if (nameLower.includes('hd') || nameLower.includes('720')) return 'HD';
    
    return 'SD';
  };

  // Função para extrair informações de séries
  const parseSeriesInfo = (item) => {
    const name = item.name || '';
    const regex = /(.+?)(?:\s+S(\d+)E(\d+))?(?:\s+\[([^\]]+)\])?$/i;
    const matches = name.match(regex);

    if (matches) {
      return {
        title: matches[1]?.trim(),
        season: matches[2] ? parseInt(matches[2]) : null,
        episode: matches[3] ? parseInt(matches[3]) : null,
        language: matches[4] || null
      };
    }

    return {
      title: name,
      season: null,
      episode: null,
      language: null
    };
  };

  useEffect(() => {
    // Verifica se o canal é favorito ao carregar
    const favorites = StorageService.getFavorites();
    setIsFavorite(favorites.some(fav => fav.id === id));
  }, [id]);

  const handlePlayPause = () => {
    setIsPlaying(!isPlaying);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    setVolume(newVolume);
  };

  const handleProgress = (played) => {
    setProgress(played);
  };

  const handleSeekBackward = () => {
    if (playerRef.current && duration) {
      const currentTime = progress * duration;
      const newTime = Math.max(0, currentTime - 15);
      playerRef.current.seekTo(newTime / duration);
    }
  };

  const handleSeekForward = () => {
    if (playerRef.current && duration) {
      const currentTime = progress * duration;
      const newTime = Math.min(duration, currentTime + 15);
      playerRef.current.seekTo(newTime / duration);
    }
  };

  const toggleFavorite = () => {
    if (isFavorite) {
      StorageService.removeFavorite(id);
      setIsFavorite(false);
    } else {
      const channel = { id }; // Adicione mais informações do canal se necessário
      StorageService.addFavorite(channel);
      setIsFavorite(true);
    }
  };

  const showDiagnostics = () => {
    if (playerRef.current) {
      playerRef.current.showDiagnostics();
    }
  };

  const handleDuration = (duration) => {
    setDuration(duration);
  };

  const handleProgressBarClick = (e) => {
    const bounds = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - bounds.left;
    const width = bounds.width;
    const percentage = x / width;
    if (playerRef.current) {
      playerRef.current.seekTo(percentage);
    }
  };

  const formatTime = (seconds) => {
    const date = new Date(seconds * 1000);
    const hh = date.getUTCHours();
    const mm = date.getUTCMinutes();
    const ss = date.getUTCSeconds().toString().padStart(2, '0');
    if (hh) {
      return `${hh}:${mm.toString().padStart(2, '0')}:${ss}`;
    }
    return `${mm}:${ss}`;
  };

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      playerWrapperRef.current.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  // Função para esconder os controles após um período de inatividade
  const startHideControlsTimer = () => {
    if (hideControlsTimeout.current) {
      clearTimeout(hideControlsTimeout.current);
    }
    setIsControlsVisible(true);
    hideControlsTimeout.current = setTimeout(() => {
      if (!isPlaying) return; // Não esconde se estiver pausado
      setIsControlsVisible(false);
    }, 3000);
  };

  // Atualiza o timer quando o mouse se move
  const handleMouseMove = () => {
    startHideControlsTimer();
  };

  // Limpa o timer quando o componente é desmontado
  useEffect(() => {
    return () => {
      if (hideControlsTimeout.current) {
        clearTimeout(hideControlsTimeout.current);
      }
    };
  }, []);

  useEffect(() => {
    const loadContent = async () => {
      try {
        setLoading(true);
        setError(null);

        if (!apiService.isLoaded) {
          await apiService.loadChannels();
        }

        const contentData = apiService.getChannel(id);
        if (!contentData) {
          throw new Error('Conteúdo não encontrado');
        }

        // Adiciona informações extras ao conteúdo
        const enhancedContent = {
          ...contentData,
          quality: detectQuality(contentData.name)
        };

        // Se for série, adiciona informações de episódio
        if (contentData.group?.toLowerCase().includes('series')) {
          const seriesInfo = parseSeriesInfo(contentData);
          Object.assign(enhancedContent, seriesInfo);
          
          // Buscar dados do TMDB para séries
          try {
            // Verificar cache primeiro
            const cacheKey = `tmdb_series_${seriesInfo.title}`;
            const cachedData = localStorage.getItem(cacheKey);
            
            if (cachedData) {
              enhancedContent.tmdbData = JSON.parse(cachedData);
            } else {
              // Buscar dados do TMDB
              const metadata = await tmdbService.getMetadata(seriesInfo.title, 'tv');
              if (metadata) {
                // Salvar no cache
                localStorage.setItem(cacheKey, JSON.stringify(metadata));
                enhancedContent.tmdbData = metadata;
              }
            }
          } catch (tmdbError) {
            console.error('Erro ao buscar dados TMDB:', tmdbError);
            // Não interrompe o fluxo se falhar a busca no TMDB
          }
        }

        setContent(enhancedContent);

        // Adiciona aos recentes
        StorageService.addRecentChannel(enhancedContent);

      } catch (error) {
        console.error('Erro ao carregar conteúdo:', error);
        setError(error.message || 'Erro ao carregar o conteúdo');
        setTimeout(() => navigate('/'), 3000);
      } finally {
        setLoading(false);
      }
    };

    loadContent();
  }, [id, navigate]);

  if (loading) {
    return (
      <Container>
        <LoadingContainer>
          <svg viewBox="0 0 100 100" xmlns="http://www.w3.org/2000/svg">
            <defs>
              <linearGradient id="gradient" x1="0%" y1="0%" x2="100%" y2="0%">
                <stop offset="0%" stopColor="#E50914" />
                <stop offset="50%" stopColor="#FF4D4D" />
                <stop offset="100%" stopColor="#E50914" />
              </linearGradient>
            </defs>
            <g transform="translate(50,50)">
              <circle r="40" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
              <circle r="40" fill="none" stroke="url(#gradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray="251.2" strokeDashoffset="125.6">
                <animateTransform 
                  attributeName="transform"
                  type="rotate"
                  from="0"
                  to="360"
                  dur="1.5s"
                  repeatCount="indefinite"
                />
              </circle>
              <circle r="30" fill="none" stroke="rgba(255,255,255,0.1)" strokeWidth="8" />
              <circle r="30" fill="none" stroke="url(#gradient)" strokeWidth="8" strokeLinecap="round" strokeDasharray="188.4" strokeDashoffset="94.2">
                <animateTransform 
                  attributeName="transform"
                  type="rotate"
                  from="360"
                  to="0"
                  dur="1s"
                  repeatCount="indefinite"
                />
              </circle>
            </g>
          </svg>
          <p>Carregando conteúdo...</p>
        </LoadingContainer>
      </Container>
    );
  }

  if (error || !content) {
    return (
      <Container>
        <Header>
          <BackButton to="/">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Voltar
          </BackButton>
        </Header>
        <ErrorMessage>
          <h2>Ops! Algo deu errado</h2>
          <p>{error || 'Não foi possível carregar o conteúdo'}</p>
        </ErrorMessage>
      </Container>
    );
  }

  const isSeriesEpisode = content.group?.toLowerCase().includes('series') && content.season && content.episode;
  const isChannel = content.group?.toLowerCase().includes('canais');

  return (
    <Container>
      <PlayerWrapper 
        ref={playerWrapperRef}
        onMouseMove={handleMouseMove}
        style={{ cursor: isControlsVisible ? 'default' : 'none' }}
      >
        <BackButton to="/" style={{ opacity: isControlsVisible ? 1 : 0 }}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M19 12H5M5 12L12 19M5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
          </svg>
          Voltar
        </BackButton>

        <Header style={{ opacity: isControlsVisible ? 1 : 0 }}>
          {/* Removido o BackButton daqui */}
        </Header>

        <PlayerSection>
          <Player
            ref={playerRef}
            channelId={id}
            detectQuality={detectQuality}
            isPlaying={isPlaying}
            volume={volume}
            onProgress={handleProgress}
            onDuration={handleDuration}
          />

          <ContentInfo style={{ opacity: isControlsVisible ? 1 : 0 }} $isChannel={isChannel}>
            <ContentPoster $isChannel={isChannel}>
              {isSeriesEpisode && content.tmdbData ? (
                <LazyImage 
                  src={tmdbService.getImageUrl(content.tmdbData.poster_path) || content.logo || '/placeholder.png'} 
                  alt={content.title}
                  onError={(e) => { 
                    if (e.target.src !== '/placeholder.png') {
                      e.target.src = '/placeholder.png';
                    }
                  }}
                />
              ) : (
                <LazyImage 
                  src={content?.logo || '/placeholder.png'} 
                  alt={content?.name}
                  onError={(e) => { 
                    if (e.target.src !== '/placeholder.png') {
                      e.target.src = '/placeholder.png';
                    }
                  }}
                />
              )}
            </ContentPoster>
            <ContentDetails $isChannel={isChannel}>
              <h2>{isSeriesEpisode ? `${content.title} - S${content.season}E${content.episode}` : content?.name}</h2>
              <p>{content?.group}</p>
            </ContentDetails>
          </ContentInfo>

          <PlayerControls style={{ opacity: isControlsVisible ? 1 : 0 }}>
            <ProgressBar 
              $progress={progress * 100} 
              onClick={handleProgressBarClick}
            />
            
            <ControlsRow>
              <div style={{ display: 'flex', alignItems: 'center', gap: '20px' }}>
                <ControlButton 
                  onClick={handleSeekBackward}
                  title="Retroceder 15 segundos"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V1L7 6L12 11V7C15.31 7 18 9.69 18 13C18 16.31 15.31 19 12 19C8.69 19 6 16.31 6 13H4C4 17.42 7.58 21 12 21C16.42 21 20 17.42 20 13C20 8.58 16.42 5 12 5Z" fill="currentColor"/>
                  </svg>
                </ControlButton>

                <ControlButton 
                  className="large"
                  onClick={handlePlayPause}
                >
                  {isPlaying ? (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M10 4H6V20H10V4Z" fill="currentColor" />
                      <path d="M18 4H14V20H18V4Z" fill="currentColor" />
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M6 4L18 12L6 20V4Z" fill="currentColor" />
                    </svg>
                  )}
                </ControlButton>

                <ControlButton 
                  onClick={handleSeekForward}
                  title="Avançar 15 segundos"
                >
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 5V1L17 6L12 11V7C8.69 7 6 9.69 6 13C6 16.31 8.69 19 12 19C15.31 19 18 16.31 18 13H20C20 17.42 16.42 21 12 21C7.58 21 4 17.42 4 13C4 8.58 7.58 5 12 5Z" fill="currentColor"/>
                  </svg>
                </ControlButton>

                <ControlButton onClick={() => setVolume(volume === 0 ? 0.5 : 0)}>
                  {volume === 0 ? (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.58579 15.0001H2C1.44772 15.0001 1 14.5523 1 14.0001V10.0001C1 9.44777 1.44772 9.00005 2 9.00005H5.58579L10.2929 4.29294C10.9229 3.66298 12 4.10915 12 5.00005V19.0001C12 19.891 10.9229 20.3371 10.2929 19.7072L5.58579 15.0001Z" fill="currentColor"/>
                      <path d="M16.2426 7.75742L21.4853 13.0001M21.4853 7.75742L16.2426 13.0001" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M5.58579 15.0001H2C1.44772 15.0001 1 14.5523 1 14.0001V10.0001C1 9.44777 1.44772 9.00005 2 9.00005H5.58579L10.2929 4.29294C10.9229 3.66298 12 4.10915 12 5.00005V19.0001C12 19.891 10.9229 20.3371 10.2929 19.7072L5.58579 15.0001Z" fill="currentColor"/>
                      <path d="M15.5355 8.46448C17.4882 10.4171 17.4882 13.5829 15.5355 15.5355M18.364 5.63603C21.8787 9.15076 21.8787 14.8492 18.364 18.364" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
                    </svg>
                  )}
                </ControlButton>

                <VolumeControl>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.01"
                    value={volume}
                    onChange={handleVolumeChange}
                  />
                </VolumeControl>

                {!isChannel && (
                  <TimeDisplay $isChannel={isChannel}>
                    {formatTime(progress * duration)} / {formatTime(duration)}
                  </TimeDisplay>
                )}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
                <ControlButton onClick={toggleFavorite}>
                  {isFavorite ? (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" fill="#FFD700"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M12 17.27L18.18 21L16.54 13.97L22 9.24L14.81 8.63L12 2L9.19 8.63L2 9.24L7.46 13.97L5.82 21L12 17.27Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </ControlButton>

                <ControlButton onClick={showDiagnostics}>
                  <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                    <path d="M12 8V12L15 15M21 12C21 16.9706 16.9706 21 12 21C7.02944 21 3 16.9706 3 12C3 7.02944 7.02944 3 12 3C16.9706 3 21 7.02944 21 12Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </ControlButton>

                <ControlButton onClick={toggleFullscreen}>
                  {isFullscreen ? (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M8 3V5H4V9H2V3H8ZM22 3V9H20V5H16V3H22ZM2 15V21H8V19H4V15H2ZM20 15V19H16V21H22V15H20Z" fill="currentColor"/>
                    </svg>
                  ) : (
                    <svg viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                      <path d="M3 8V3H8M3 16V21H8M21 8V3H16M21 16V21H16" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  )}
                </ControlButton>
              </div>
            </ControlsRow>
          </PlayerControls>
        </PlayerSection>
      </PlayerWrapper>
      
      <InfoSection>
        <ContentHeader>
          {isChannel ? (
            <ChannelLogo>
              <LazyImage 
                src={content.logo || '/placeholder.png'} 
                alt={content.name}
                onError={(e) => { 
                  if (e.target.src !== '/placeholder.png') {
                    e.target.src = '/placeholder.png';
                  }
                }}
              />
            </ChannelLogo>
          ) : isSeriesEpisode && content.tmdbData ? (
            <ContentPoster $isChannel={false}>
              <LazyImage 
                src={tmdbService.getImageUrl(content.tmdbData.poster_path) || content.logo || '/placeholder.png'} 
                alt={content.title}
                onError={(e) => { 
                  if (e.target.src !== '/placeholder.png') {
                    e.target.src = '/placeholder.png';
                  }
                }}
              />
            </ContentPoster>
          ) : (
            <ContentPoster $isChannel={false}>
              <LazyImage 
                src={content.logo || '/placeholder.png'} 
                alt={content.name}
                onError={(e) => { 
                  if (e.target.src !== '/placeholder.png') {
                    e.target.src = '/placeholder.png';
                  }
                }}
              />
            </ContentPoster>
          )}
          
          <ContentInfo>
            <ContentTitle>
              {isSeriesEpisode ? content.title : content.name}
              {isSeriesEpisode && ` - S${content.season}E${content.episode}`}
            </ContentTitle>
            <ContentMeta>
              <QualityBadge $quality={content.quality}>
                {content.quality}
              </QualityBadge>
              <CategoryBadge>{content.group}</CategoryBadge>
              {content.language && (
                <CategoryBadge>{content.language}</CategoryBadge>
              )}
            </ContentMeta>
          </ContentInfo>
        </ContentHeader>
      </InfoSection>
    </Container>
  );
};

export default WatchChannel;